import classNames from 'classnames';
import { ButtonColor } from './Button';

export type ButtonSize = 'small' | 'medium' | 'large';
export type IconButtonSize = 'x-small' | 'small' | 'medium' | 'large';

export type BaseProps = {
  color: ButtonColor;
  darkMode?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
  'data-testid'?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'data-hj-suppress'?: boolean;
  children: string | string[];
};

export type BaseButtonProps = BaseProps & {
  icon?: React.FunctionComponent<{ className?: string }>;
  size?: ButtonSize;
};

export type BaseIconButtonProps = BaseProps & {
  icon: React.FunctionComponent<{ className?: string }>;
  size?: IconButtonSize;
};

export type BaseMotorButtonProps = BaseProps & {
  icon: React.FunctionComponent<{ className?: string }>;
  size: ButtonSize;
};

export type BaseLinkProps = {
  url: string;
  target?: React.HTMLAttributeAnchorTarget;
};

export const buttonIconSizeStyle = (size?: ButtonSize) => {
  if (size === 'small') return 'w-[18px] h-[18px]';
  if (size === 'medium') return 'w-5 h-5';
  if (size === 'large') return 'w-6 h-6';
  return 'w-5 768:w-6 768:h-6';
};

export function iconButtonSizeStyle(size?: IconButtonSize) {
  if (size === 'x-small') return 'w-[28px] h-[28px]';
  if (size === 'small') return 'w-8 h-8';
  if (size === 'medium') return 'w-10 h-10';
  return 'w-10 h-10 768:w-12 768:h-12';
}

export function iconButtonIconSizeStyle(size?: IconButtonSize) {
  if (size === 'x-small') return `w-4 h-4`;
  return buttonIconSizeStyle(size);
}

export function buttonSizeStyle(
  size: ButtonSize | undefined,
  leftIcon: boolean,
  rightIcon: boolean
) {
  const sizes = buttonSizes[size ?? 'responsive'];
  return classNames(
    sizes.size,
    leftIcon ? sizes.padding.left.icon : sizes.padding.left.noIcon,
    rightIcon ? sizes.padding.right.icon : sizes.padding.right.noIcon
  );
}

export type ButtonSizes = {
  size: string;
  padding: {
    left: {
      icon: string;
      noIcon: string;
    };
    right: {
      icon: string;
      noIcon: string;
    };
  };
};

const buttonSizes: Record<ButtonSize | 'responsive', ButtonSizes> = {
  large: {
    size: 'text-button-label-l h-12',
    padding: {
      left: {
        icon: 'pl-5',
        noIcon: 'pl-8',
      },
      right: {
        icon: 'pr-5',
        noIcon: 'pr-8',
      },
    },
  },
  medium: {
    size: 'text-button-label-m h-10',
    padding: {
      left: {
        icon: 'pl-4',
        noIcon: 'pl-6',
      },
      right: {
        icon: 'pr-4',
        noIcon: 'pr-6',
      },
    },
  },
  small: {
    size: 'text-button-label-s h-8',
    padding: {
      left: {
        icon: 'pl-3',
        noIcon: 'pl-4',
      },
      right: {
        icon: 'pr-3',
        noIcon: 'pr-4',
      },
    },
  },
  responsive: {
    size: 'text-button-label-m h-10 768:text-button-label-l 768:h-12',
    padding: {
      left: {
        icon: 'pl-4 768:pl-5',
        noIcon: 'pl-6 768:pl-8',
      },
      right: {
        icon: 'pr-4 768:pr-5',
        noIcon: 'pr-6 768:pr-8',
      },
    },
  },
};

export type ButtonStates = {
  lightMode: {
    enabled: string;
    hover: string;
    pressed: string;
    disabled: string;
    focus: string;
  };
  darkMode: {
    enabled: string;
    hover: string;
    pressed: string;
    disabled: string;
    focus: string;
  };
};

export const stateStyles: Record<ButtonColor, ButtonStates> = {
  primary: {
    lightMode: {
      enabled: 'bg-midnight text-white',
      hover: 'hover:bg-neutral-800 group-hover:bg-neutral-800',
      pressed: 'active:bg-neutral-800',
      disabled: 'disabled:text-neutral-500 disabled:bg-neutral-300',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-light focus-visible:outline-offset-2',
    },
    darkMode: {
      enabled: 'bg-white text-midnight',
      hover: 'hover:bg-neutral-300 group-hover:bg-neutral-300',
      pressed: 'active:bg-neutral-300',
      disabled: 'disabled:text-neutral-500 disabled:bg-neutral-800',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-dark focus-visible:outline-offset-2',
    },
  },
  secondary: {
    lightMode: {
      enabled: 'bg-opacity-midnight-200 text-midnight',
      hover: 'hover:bg-midnight-100 group-hover:bg-midnight-100',
      pressed: 'active:bg-midnight-100',
      disabled: 'disabled:text-neutral-500 disabled:bg-neutral-300',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-light focus-visible:outline-offset-2',
    },
    darkMode: {
      enabled: 'bg-opacity-white-400 text-white',
      hover: 'hover:bg-opacity-white-200 group-hover:bg-opacity-white-200',
      pressed: 'active:bg-opacity-white-200',
      disabled: 'disabled:text-neutral-500 disabled:bg-neutral-800',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-dark focus-visible:outline-offset-2',
    },
  },
  'on-image': {
    lightMode: {
      enabled: 'bg-opacity-midnight-700 text-white',
      hover: 'hover:bg-white/[0.08] group-hover:bg-white/[0.08]',
      pressed: 'active:bg-white/[0.08]',
      disabled: 'disabled:text-neutral-500 disabled:bg-transparent',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-dark focus-visible:outline-offset-2',
    },
    darkMode: {
      enabled: 'bg-opacity-midnight-700 text-white',
      hover: 'hover:bg-white/[0.08] group-hover:bg-white/[0.08]',
      pressed: 'active:bg-white/[0.08]',
      disabled: 'disabled:text-neutral-500 disabled:bg-transparent',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-dark focus-visible:outline-offset-2',
    },
  },
  tertiary: {
    lightMode: {
      enabled: 'text-midnight',
      hover: 'hover:bg-neutral-100 group-hover:bg-neutral-100',
      pressed: 'active:bg-neutral-100',
      disabled: 'disabled:text-neutral-500 disabled:bg-transparent',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-light focus-visible:outline-offset-2',
    },
    darkMode: {
      enabled: 'text-white',
      hover: 'hover:bg-white/[0.08] group-hover:bg-white/[0.08]',
      pressed: 'active:bg-white/[0.08]',
      disabled: 'disabled:text-neutral-500 disabled:bg-transparent',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-dark focus-visible:outline-offset-2',
    },
  },
  'primary-red': {
    lightMode: {
      enabled: 'bg-primary-tv2-red text-white',
      hover: 'hover:bg-interactive-tv2-red-on-light group-hover:bg-interactive-tv2-red-on-light',
      pressed: 'active:bg-interactive-tv2-red-on-light',
      disabled: 'disabled:text-neutral-500 disabled:bg-neutral-300',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-light focus-visible:outline-offset-2',
    },
    darkMode: {
      enabled: 'bg-primary-tv2-red text-white',
      hover: 'hover:bg-interactive-tv2-red-on-dark group-hover:bg-interactive-tv2-red-on-dark',
      pressed: 'active:bg-interactive-tv2-red-on-dark',
      disabled: 'disabled:text-neutral-500 disabled:bg-opacity-white-200',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-dark focus-visible:outline-offset-2',
    },
  },
  'primary-green': {
    lightMode: {
      enabled: 'bg-semantic-success-on-light text-white',
      hover: 'hover:bg-interactive-green-on-dark group-hover:bg-interactive-green-on-dark',
      pressed: 'active:bg-interactive-green-on-dark',
      disabled: 'disabled:text-neutral-500 disabled:bg-neutral-300',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-light focus-visible:outline-offset-2',
    },
    darkMode: {
      enabled: 'bg-semantic-success-on-light text-white',
      hover: 'hover:bg-interactive-green-on-dark group-hover:bg-interactive-green-on-dark',
      pressed: 'active:bg-interactive-green-on-dark',
      disabled: 'disabled:text-neutral-500 disabled:bg-neutral-800',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-dark focus-visible:outline-offset-2',
    },
  },
  breaking: {
    lightMode: {
      enabled: 'bg-ds-p-yellow-500 text-black',
      hover: 'hover:bg-ds-p-yellow-600 group-hover:bg-ds-p-yellow-600',
      pressed: 'active:bg-ds-p-yellow-600',
      disabled: 'disabled:text-neutral-500 disabled:bg-neutral-300',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-light focus-visible:outline-offset-2',
    },
    darkMode: {
      enabled: 'bg-ds-p-yellow-500 text-black',
      hover: 'hover:bg-ds-p-yellow-600 group-hover:bg-ds-p-yellow-600',
      pressed: 'active:bg-ds-p-yellow-600',
      disabled: 'disabled:text-neutral-500 disabled:bg-neutral-800',
      focus:
        'outline-none focus-visible:outline-2 focus-visible:outline-semantic-links-on-dark focus-visible:outline-offset-2',
    },
  },
};
